/**
 * Scoring-STAGE runtime contract — feature E07-F01 (the per-request `scored`
 * stage, T02/T03/T05/T06/T08/T09). This file is the runtime sibling of
 * `research-scoring.ts` (the pure math + REST surface): it carries the shapes the
 * scoring STAGE produces/consumes when it walks the long-list in batches of
 * 20-30, calls the DB-loaded `research_score_candidate` prompt, applies the
 * extract-division (T08) and Grade-C-filler (T09) policies, and upserts each row.
 *
 * 1:1 port note (matches the headers in research-scoring.ts /
 * researchScoring.ts): Company-Research Stage-2 was NEVER deployed to legacy n8n
 * prod, so there is no verbatim SQL for the stage loop. The authoritative source
 * is the research prototype `../EER app/prototype/assets/research.data.js`
 * (MOCK.companies — the 15 hand-graded rows: Cynet A, Arctic Wolf A founded=fail,
 * Sygnia B near-miss, NTT/Microsoft division-of ~600/~800 units, Pentera/
 * Seemplicity Grade-C filler, SecHerd/BlueOps/Talon needs_human) +
 * company-research-architecture.md §6.16/§7/§9 + ADR 2026-06-02 (request-driven
 * grading; services_match is ONE weighted signal). The prompt BODY is loaded from
 * `ai_prompts` (key `research_score_candidate` / `research_grade_explain` — seeded
 * in prisma/seed-data.ts); a hard-coded prompt body is FORBIDDEN here.
 *
 * Feature-owned: re-exported from `@eer/shared` by the serial Wire pass. It MUST
 * NOT edit the shared barrel (packages/shared/src/index.ts) during parallel work.
 */
import { z } from 'zod';
import { scoringBreakdownSchema, scoringGradeSchema, hardGateInputSchema, needsHumanSignalsSchema, divisionContextSchema, weightProfileSchema, } from './research-scoring.js';
// ---------------------------------------------------------------------------
// Prompt keys (ai_prompts) — prompts-only-from-DB, NO code fallback
// ---------------------------------------------------------------------------
/**
 * `ai_prompts` key for the per-batch scoring prompt (T02). The prompt BODY lives
 * ONLY in the DB row (seeded in prisma/seed-data.ts); a hard-coded body is
 * FORBIDDEN — the stage runner loads it via buildPromptFromDb.
 */
export const RESEARCH_SCORE_CANDIDATE_PROMPT_KEY = 'research_score_candidate';
/**
 * `ai_prompts` key for the one-line grade rationale prompt (T04 — research_grade
 * _explain). Same prompts-only-from-DB rule as above.
 */
export const RESEARCH_GRADE_EXPLAIN_PROMPT_KEY = 'research_grade_explain';
// ---------------------------------------------------------------------------
// Stage identity + batch sizing (T02)
// ---------------------------------------------------------------------------
/** The final pipeline stage name (research_companies.stage). */
export const SCORING_STAGE_NAME = 'scored';
/** When a row exhausts its retries it lands here (architecture §4: cap=3). */
export const SCORING_FAILED_STAGE = 'failed';
/** Batch window the scoring stage walks the long-list in (acceptance: 20-30). */
export const SCORING_STAGE_BATCH_MIN = 20;
export const SCORING_STAGE_BATCH_MAX = 30;
/** Default batch size used when none is supplied (mid-window). */
export const SCORING_STAGE_BATCH_DEFAULT = 25;
/**
 * Clamp a requested batch size into the legal [20,30] window. A caller that asks
 * for 5 gets 20, for 1000 gets 30, for nothing gets the default 25.
 */
export function clampScoringBatch(requested) {
    if (requested == null || Number.isNaN(requested))
        return SCORING_STAGE_BATCH_DEFAULT;
    const n = Math.floor(requested);
    if (n < SCORING_STAGE_BATCH_MIN)
        return SCORING_STAGE_BATCH_MIN;
    if (n > SCORING_STAGE_BATCH_MAX)
        return SCORING_STAGE_BATCH_MAX;
    return n;
}
// ---------------------------------------------------------------------------
// One long-list row fed into the scoring stage (read off research_companies +
// the disambiguation / verify partials already stored on the row)
// ---------------------------------------------------------------------------
/** The minimal `research_companies`-row shape the scoring stage consumes. */
export const scoringStageRowSchema = z.object({
    /** research_companies.id — the long-list row primary key. */
    id: z.number().int().positive(),
    /** request_id — the run this row belongs to. */
    requestId: z.number().int().positive(),
    /** company_id once dedup resolved it (null pre-dedup / for manual rows). */
    companyId: z.number().int().positive().nullable().default(null),
    /** Display name (raw_name or the resolved company name). */
    name: z.string(),
    /** Per-criterion verdicts produced by the verify stage. */
    breakdown: scoringBreakdownSchema.default({}),
    /** Binary hard-gate inputs (geo radius / size cap / DNS). */
    gate: hardGateInputSchema.partial().optional(),
    /** Structured needs_human signals collected upstream (verify/enrich). */
    needsHumanSignals: needsHumanSignalsSchema.partial().optional(),
    /** Disambiguation context — steers scoring onto the extracted unit (T08). */
    division: divisionContextSchema.partial().optional(),
    /** Base confidence carried from the verify stage (0..1); null ⇒ unscored. */
    confidence: z.number().min(0).max(1).nullable().default(null),
    /** True when a specialist added the row by hand (Talon — may stay unscored). */
    isManual: z.boolean().default(false),
});
// ---------------------------------------------------------------------------
// The recruiter's request-level scoring context (the run snapshot)
// ---------------------------------------------------------------------------
/**
 * The per-request scoring context. `weights` is the SNAPSHOT taken at run-start
 * (research_requests.weight_profile — T06), NOT the live global default, so a
 * later default change never re-scores a past run.
 */
export const scoringStageContextSchema = z.object({
    /** Snapshot of the weight profile for THIS run (T06). */
    weights: weightProfileSchema,
    /** Active grade scope from intake (criteria.gradeScope) — A | A+B | A+B+C. */
    gradeScope: z.enum(['A', 'A+B', 'A+B+C']).default('A+B+C'),
    /** Optional explicit score bands (recruiter override of the 80/60/40 cuts). */
    bands: z
        .object({ a: z.number().optional(), b: z.number().optional(), c: z.number().optional() })
        .default({}),
    /** Recruiter Grade A/B/C definitions + free text (request-tied rationale). */
    gradeDefinitions: z
        .object({ A: z.string().optional(), B: z.string().optional(), C: z.string().optional() })
        .default({}),
    freeText: z.string().optional(),
});
// ---------------------------------------------------------------------------
// One scored row (the patch the stage upserts onto research_companies)
// ---------------------------------------------------------------------------
/** The resolved scoring decision for ONE long-list row. */
export const scoringStageDecisionSchema = z.object({
    rowId: z.number().int().positive(),
    companyId: z.number().int().positive().nullable(),
    name: z.string(),
    /** Final stage after scoring (scored | failed). */
    stage: z.enum([SCORING_STAGE_NAME, SCORING_FAILED_STAGE]),
    /** 0..100 NUMERIC(5,2); null when hard-excluded / unscored / manual. */
    score: z.number().min(0).max(100).nullable(),
    /** A|B|C|null (null = excluded / below the cut / scope-clipped). */
    grade: scoringGradeSchema.nullable(),
    breakdown: scoringBreakdownSchema,
    /** confidence NUMERIC(4,3) 0..1; null when unscored. */
    confidence: z.number().min(0).max(1).nullable(),
    needsHuman: z.boolean(),
    /** One-line request-tied rationale (research_grade_explain or the fallback). */
    rationale: z.string(),
    /** True when a hard gate (geo / size cap / DNS) excluded the row. */
    excluded: z.boolean(),
    /** True when the scored unit is an extracted division, not the parent (T08). */
    scopedToDivision: z.boolean(),
    /** True when this row is an auto Grade-C long-list filler (T09). */
    isGradeCFiller: z.boolean(),
});
/** The aggregate result of running the scoring stage over a batch. */
export const scoringStageResultSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    requestId: z.number().int().positive(),
    /** How many rows were scored (stage='scored'). */
    scored: z.number().int().min(0),
    /** How many rows exhausted retries (stage='failed'). */
    failed: z.number().int().min(0),
    /** How many rows were hard-excluded (kept off the long-list). */
    excluded: z.number().int().min(0),
    /** How many Grade-C filler rows were emitted (T09). */
    cFillers: z.number().int().min(0),
    /** The per-row decisions (ordered by score DESC NULLS LAST, the rank index). */
    decisions: z.array(scoringStageDecisionSchema),
});
// ---------------------------------------------------------------------------
// research_score_candidate — the LLM batch I/O shape (parsed from the prompt out)
// ---------------------------------------------------------------------------
/** One element of the JSON array the research_score_candidate prompt returns. */
export const scoreCandidateLlmItemSchema = z.object({
    /** Echo of the row id (so a re-ordered LLM array still maps back correctly). */
    rowId: z.number().int().positive().optional(),
    name: z.string().optional(),
    breakdown: scoringBreakdownSchema.default({}),
    /** The model's own confidence in its per-criterion verdicts. */
    confidence: z.number().min(0).max(1).optional(),
    /** Optional one-line rationale (else research_grade_explain fills it). */
    rationale: z.string().optional(),
});
/** The prompt returns a bare array of items (one per company in the batch). */
export const scoreCandidateLlmArraySchema = z.array(scoreCandidateLlmItemSchema);
//# sourceMappingURL=research-scoring-stage.js.map